/**
 * Painel "Ações disparadas" — Bloco D.2 (UI construtor de questionário).
 *
 * Mostrado no editor de pergunta (PerguntaDialog em QuestionariosPage).
 * Lista automations com trigger_type='questionario_resposta' filtradas por
 * (templateId, perguntaCodigo).
 *
 * **BUG-V2-058 / Sprint 3 (2026-05-25)**: ganhou botão "+ Nova ação" inline.
 * Antes era só read-only com link "Gerenciar →" pra `/admin/automacoes` que
 * NÃO tem botão Nova — loop morto. Agora abre `<AutomationFormDialog>` shared
 * com `contexto={kind:'questionario', templateId, perguntaCodigo, workflowSlug}`.
 *
 * Link "Gerenciar →" continua disponível pra visão global (read-only +
 * filtros), mas usuário não precisa mais sair daqui pra criar.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { AutomationFormDialog } from '@/components/automations/AutomationFormDialog';
// FASE-C: rótulo das ações vem da fonte única (a mesma do editor de ações).
// Antes esta tela tinha a própria lista, com "WhatsApp (stub)", "Email (stub)",
// "Criar card em outro workflow" e "Agendar no calendar".
import {
  ACTION_TYPE_LABELS,
  type AutomationActionType,
} from '@/components/automations/AcoesEditor';
import type { CreateAutomationInput } from '@/lib/automations-store';
import { useWorkflows } from '@/lib/workflows-store';

interface AutomationSummary {
  id: string;
  nome: string;
  workflowSlug: string;
  trigger: {
    type: string;
    config: { templateId?: string; perguntaCodigo?: string; valorEsperado?: string | string[] | null };
  };
  actions: Array<{ type: string }>;
  enabled: boolean;
}

interface AcoesDisparadasPanelProps {
  templateId: string;
  perguntaCodigo: string;
}

function actionLabel(type: string): string {
  return ACTION_TYPE_LABELS[type as AutomationActionType] ?? 'Ação configurada';
}

export function AcoesDisparadasPanel({ templateId, perguntaCodigo }: AcoesDisparadasPanelProps) {
  const qc = useQueryClient();
  // BUG-V2-058: workflows pra resolver workflowSlug default. Manutenção é
  // o caso mais comum pra trigger_type='questionario_resposta'. Se inexistente,
  // fallback pro primeiro workflow ativo.
  const { data: workflows = [] } = useWorkflows({ ativo: true });
  const defaultWorkflowSlug =
    workflows.find((w) => w.slug === 'manutencao' || w.deptSlug === 'manutencao')?.slug ??
    workflows[0]?.slug ??
    'manutencao';

  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: automations, isLoading } = useQuery<AutomationSummary[]>({
    queryKey: ['automations-questionario', templateId, perguntaCodigo],
    queryFn: () =>
      api.get<AutomationSummary[]>(
        `/api/v1/automations?templateId=${encodeURIComponent(templateId)}&perguntaCodigo=${encodeURIComponent(perguntaCodigo)}`,
      ),
    enabled: Boolean(templateId && perguntaCodigo),
    staleTime: 30_000,
  });

  // BUG-V2-058: handler de criação inline. Backend `/api/v1/automations` (mesmo
  // do workflow). O wizard interno do WorkflowAutomationForm ainda usa triggers
  // kanban — pra Sprint 3 escopo enxuto, o usuário cria a base e edita o trigger
  // depois via /admin/automacoes (Sub-fase futura: estender wizard pra questionario_resposta).
  async function handleSubmit(input: Omit<CreateAutomationInput, 'workflowSlug'>) {
    await api.post('/api/v1/automations', {
      ...input,
      workflowSlug: defaultWorkflowSlug,
    });
    // Invalida cache da lista — re-fetch atualiza UI.
    await qc.invalidateQueries({
      queryKey: ['automations-questionario', templateId, perguntaCodigo],
    });
    setDialogOpen(false);
  }

  return (
    <div
      data-testid="acoes-disparadas-panel"
      className="mt-4 min-w-0 rounded-xl border border-line p-3 dark:border-line-dark"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
          Ações disparadas
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDialogOpen(true)}
            data-testid="acoes-disparadas-nova"
          >
            + Nova ação
          </Button>
          <a
            href="/admin/automacoes"
            className="text-xs text-ink-2 underline-offset-2 hover:underline dark:text-ink-dark-2"
            data-testid="link-gerenciar-automacoes"
          >
            Ver todas
          </a>
        </div>
      </div>

      {isLoading && (
        <p
          data-testid="acoes-loading"
          className="text-xs text-ink-2 dark:text-ink-dark-2"
        >
          Carregando ações…
        </p>
      )}

      {!isLoading && (!automations || automations.length === 0) && (
        <p
          data-testid="acoes-empty"
          className="text-xs text-ink-2 dark:text-ink-dark-2"
        >
          Nenhuma ação configurada pra esta pergunta. Clique em{' '}
          <strong>+ Nova ação</strong> pra criar agora.
        </p>
      )}

      {/* BUG-V2-058 / Sprint 3: dialog inline pra criar ação contextual.
          Sub-fase 6: form nativo de questionário — não precisa mais de
          columns/deals/getColumnLabel (shell aceita props omitidas pra
          kind='questionario'). */}
      <AutomationFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        contexto={{
          kind: 'questionario',
          workflowSlug: defaultWorkflowSlug,
          templateId,
          perguntaCodigo,
        }}
        onSubmit={handleSubmit}
      />

      {automations && automations.length > 0 && (
        <ul data-testid="acoes-list" className="space-y-2">
          {automations.map((a) => (
            <li
              key={a.id}
              data-testid={`acao-${a.id}`}
              className="flex min-w-0 items-start gap-2 text-xs"
            >
              {/* FASE-C: os emojis ✅/⏸️ viraram o Badge canônico (o design
                  system é Lucide-only e o emoji não tinha rótulo acessível). */}
              <Badge
                variant={a.enabled ? 'success' : 'neutral'}
                size="sm"
                data-testid={`acao-${a.id}-estado`}
              >
                {a.enabled ? 'Ativa' : 'Pausada'}
              </Badge>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink-1 dark:text-ink-dark-1">
                  {a.nome}
                </div>
                <div className="text-ink-2 dark:text-ink-dark-2">
                  {a.actions.length === 0
                    ? 'Nenhuma ação configurada'
                    : a.actions.map((act) => actionLabel(act.type)).join(' · ')}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
